import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import Button from 'components/Button';
import { FlyoutContext, FlyoutProvider } from 'components/Flyout/FlyoutContext';
import LoadingDots from 'components/LoadingDots';
import Message from 'components/Message';
import { createSuccessSnackbar } from 'components/Snackbar';
import SvgDownload from 'components/Svgs/Download/Download';
import { Title } from 'features/App';
import { bem } from 'helpers/class-name';
import { selectIsMobile } from 'selectors/ui';

import { triggerBrowserDownload } from '../shared/SkuCsvDownload.helpers';
import useSkuVenues from '../shared/useSkuVenues';

import {
  buildSearchSuggestions,
  collectFilterOptions,
  filterItems,
  toItemsReportFilter,
  UNCATEGORIZED_KEY,
} from './SkuItemReports.filters';
import { useItemsReportQuery } from './SkuItemReports.hooks';
import messages from './SkuItemReports.messages';
import { buildItemsReportCsv, itemsReportCsvFileName } from './SkuItemReportsCsv.helpers';
import SkuItemReportsFlyout, { SKU_ITEM_REPORTS_FLYOUT_ID } from './SkuItemReportsFlyout';
import SkuItemReportsLocalFilters from './SkuItemReportsLocalFilters';
import SkuItemReportsTable from './SkuItemReportsTable';
import { DATE_RANGE_PRESET, findMatchingPreset, presetRange } from './SkuOrderReports.filters';
import SkuOrderReportsFilters from './SkuOrderReportsFilters';
import { FILTER_TYPE, makeReportsTracker, REPORTS_PAGE, SkuReportsTrackingContext } from './SkuReports.tracking';
import SummaryTile, { SummaryTileEntries } from './SkuReportsSummaryTile';
import './SkuItemReports.scss';

const { block, element } = bem('sku-item-reports');

const EMPTY_VALUE = '—';

// One settled search event per query, not one per keystroke.
const SEARCH_TRACKING_DEBOUNCE = 1200;

const SkuItemReportsPageContent = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const isMobile = useSelector(selectIsMobile);
  const skuVenues = useSkuVenues();
  const showLocationColumn = skuVenues.length > 1;

  const [selectedVenueIds, setSelectedVenueIds] = useState(null);
  const [dateFilter, setDateFilter] = useState(() => ({
    preset: DATE_RANGE_PRESET.LAST_7_DAYS,
    range: presetRange(DATE_RANGE_PRESET.LAST_7_DAYS),
  }));
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedMenus, setSelectedMenus] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const { openFlyout } = useContext(FlyoutContext);

  const venueIds = useMemo(
    () => selectedVenueIds ?? skuVenues.map(({ venueId }) => venueId),
    [selectedVenueIds, skuVenues]
  );
  const filter = toItemsReportFilter({ venueIds, range: dateFilter.range });

  const tracking = useMemo(
    () => makeReportsTracker({ page: REPORTS_PAGE.ITEMS, venueIds, isMobile }),
    [venueIds, isMobile]
  );
  useEffect(() => {
    tracking.pageViewed();
  }, []);

  const { data: report, isInitialLoading, isError, refetch } = useItemsReportQuery(filter);

  const allItems = report?.items || [];
  const filterOptions = collectFilterOptions(allItems);
  const categories = selectedCategories.filter(
    category =>
      filterOptions.categories.includes(category) || (category === UNCATEGORIZED_KEY && filterOptions.hasUncategorized)
  );
  const menus = selectedMenus.filter(menu => filterOptions.menus.includes(menu));
  const items = filterItems(allItems, { categories, menus, search });
  const suggestions = buildSearchSuggestions(filterItems(allItems, { categories, menus }), search);

  // Sellers come one per currency, and a units-only subtitle can't tell the entries apart, so the
  // subtitle names the currency — but only then, keeping the usual single-currency card on-design.
  const hasMultipleSellerCurrencies = (report?.topSellers?.length || 0) > 1;

  const handleItemOpen = (venueItem, entryPoint) => {
    tracking.rowOpened({ entryPoint, 'item id': venueItem.itemId });
    setSelectedItem(venueItem);
    openFlyout(SKU_ITEM_REPORTS_FLYOUT_ID);
  };

  const handleCategoriesChange = nextCategories => {
    if (nextCategories.length === 0) tracking.filterRemoved(FILTER_TYPE.CATEGORY);
    else tracking.filterApplied(FILTER_TYPE.CATEGORY, { 'categories selected': nextCategories.length });
    setSelectedCategories(nextCategories);
  };

  const handleMenusChange = nextMenus => {
    if (nextMenus.length === 0) tracking.filterRemoved(FILTER_TYPE.MENU);
    else tracking.filterApplied(FILTER_TYPE.MENU, { 'menus selected': nextMenus.length });
    setSelectedMenus(nextMenus);
  };

  const searchMetaRef = useRef({});
  const handleSearchChange = (value, meta = {}) => {
    searchMetaRef.current = meta;
    setSearch(value);
  };
  // The items of each render are the results for that render's search value.
  useEffect(() => {
    if (!search.trim()) return undefined;
    const timeout = setTimeout(() => {
      tracking.searchApplied({
        suggestionSelected: Boolean(searchMetaRef.current.fromSuggestion),
        resultCount: items.length,
      });
    }, SEARCH_TRACKING_DEBOUNCE);
    return () => clearTimeout(timeout);
  }, [search]);

  const renderTileValue = content => {
    if (isInitialLoading) return <LoadingDots />;
    if (!content) return EMPTY_VALUE;
    return content;
  };

  const handleCsvExport = () => {
    triggerBrowserDownload(new Blob([buildItemsReportCsv(items)]), itemsReportCsvFileName());
    tracking.csvDownloaded({
      rowsExported: items.reduce((count, item) => count + item.venueItems.length, 0),
      isFiltered:
        categories.length > 0 || menus.length > 0 || search.trim().length > 0 || venueIds.length !== skuVenues.length,
      filters: {
        'categories selected': categories.length,
        'menus selected': menus.length,
        'search active': search.trim().length > 0,
        'venues selected': venueIds.length,
      },
    });
    dispatch(createSuccessSnackbar(messages.csvExportStarted));
  };

  return (
    <SkuReportsTrackingContext.Provider value={tracking}>
      <Message message={messages.pageTitle} as={Title} />

      <div className={block()}>
        <SkuOrderReportsFilters
          venues={skuVenues}
          selectedVenueIds={venueIds}
          onVenuesChange={setSelectedVenueIds}
          range={dateFilter.range}
          onRangeChange={range => setDateFilter({ preset: findMatchingPreset(range), range })}
          preset={dateFilter.preset}
          onPresetSelect={preset => setDateFilter({ preset, range: presetRange(preset) })}
        />

        <div className={element('tiles')}>
          <SummaryTile
            label={<Message message={messages.topSellerTile} />}
            value={renderTileValue(
              report?.topSellers?.length ? (
                <SummaryTileEntries
                  entries={report.topSellers}
                  renderSubtitle={entry =>
                    hasMultipleSellerCurrencies ? (
                      <Message
                        message={messages.topSellerSubtitleWithCurrency}
                        values={{ count: entry.unitsSold, currency: entry.earnings?.currencyCode }}
                      />
                    ) : (
                      <Message message={messages.topSellerSubtitle} values={{ count: entry.unitsSold }} />
                    )
                  }
                />
              ) : null
            )}
          />
          <SummaryTile
            label={<Message message={messages.topEarnerTile} />}
            value={renderTileValue(
              report?.topEarners?.length ? (
                <SummaryTileEntries
                  entries={report.topEarners}
                  renderSubtitle={entry => (
                    <Message
                      message={messages.topEarnerSubtitle}
                      values={{
                        amount: (
                          <Message
                            format="currency"
                            currency={entry.earnings?.currencyCode}
                            value={entry.earnings?.amount}
                          />
                        ),
                      }}
                    />
                  )}
                />
              ) : null
            )}
          />
        </div>

        <div className={element('listing-header')}>
          <span className={element('listing-count')}>
            {report && <Message message={messages.itemsCount} values={{ count: items.length }} />}
          </span>
          {items.length > 0 && (
            <Button
              styled="inline-link"
              className={element('csv-button')}
              onClick={handleCsvExport}
              aria-label={intl.formatMessage(messages.downloadCsv)}>
              <SvgDownload className={element('csv-icon')} />
              {!isMobile && <Message message={messages.downloadCsv} />}
            </Button>
          )}
        </div>

        <SkuItemReportsLocalFilters
          options={filterOptions}
          categories={categories}
          onCategoriesChange={handleCategoriesChange}
          menus={menus}
          onMenusChange={handleMenusChange}
          search={search}
          onSearchChange={handleSearchChange}
          suggestions={suggestions}
        />

        <SkuItemReportsTable
          items={items}
          filtersKey={JSON.stringify([filter, categories, menus, search])}
          showLocationColumn={showLocationColumn}
          isMobile={isMobile}
          isLoading={isInitialLoading}
          isError={isError}
          onRetry={() => refetch()}
          onItemOpen={handleItemOpen}
        />
      </div>

      <SkuItemReportsFlyout item={selectedItem} />
    </SkuReportsTrackingContext.Provider>
  );
};

const SkuItemReportsPage = () => (
  <FlyoutProvider>
    <SkuItemReportsPageContent />
  </FlyoutProvider>
);

export default SkuItemReportsPage;
